import { getApiUrl, API_CONFIG } from "../config/api";

export class InviteService {
  static async getAuthHeaders(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  // Send an invite
  static async sendInvite(inviteData, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(getApiUrl("invites"), {
      method: "POST",
      headers,
      body: JSON.stringify(inviteData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to send invite");
    }

    return response.json();
  }

  // Check pending invites by email (public endpoint - not prefixed with /api)
  static async checkInvitesByEmail(email) {
    // For public routes, we need to construct the URL without /api prefix
    // Remove /api from base URL if it exists, then add our public route
    const baseUrl = API_CONFIG.BASE_URL;
    const publicBaseUrl = baseUrl.replace(/\/api$/, "");
    const response = await fetch(
      `${publicBaseUrl}/check-invites/${encodeURIComponent(email)}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to check pending invites");
    }

    return response.json();
  }

  // Accept an invite by ID (requires authentication)
  static async acceptInvite(inviteId, acceptanceData, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    const response = await fetch(getApiUrl(`invites/accept/${inviteId}`), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(acceptanceData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to accept invite");
    }

    return response.json();
  }

  // Get organization invites
  static async getOrganizationInvites(
    organizationId,
    params = {},
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const queryParams = new URLSearchParams(params);
    const url = `invites/organization/${organizationId}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(getApiUrl(url), {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get organization invites");
    }

    return response.json();
  }

  // Get business unit invites
  static async getBusinessUnitInvites(
    businessUnitId,
    params = {},
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const queryParams = new URLSearchParams(params);
    const url = `invites/business-unit/${businessUnitId}${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

    const response = await fetch(getApiUrl(url), {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get business unit invites");
    }

    return response.json();
  }

  // Cancel an invite
  static async cancelInvite(inviteId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(getApiUrl(`invites/${inviteId}`), {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to cancel invite");
    }

    return response.json();
  }

  // Resend an invite
  static async resendInvite(inviteId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(getApiUrl(`invites/${inviteId}/resend`), {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to resend invite");
    }

    return response.json();
  }
}
